import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { useOrg } from './lib/org';
import Shell from './components/Shell';
import Login from './pages/Login';
import Companies from './pages/Companies';
import InvoicePublic from './pages/InvoicePublic';
import ContactStatement from './pages/ContactStatement';
import ReportAging from './pages/ReportAging';
import Services from './pages/Services';
import Quotes from './pages/Quotes';
import QuoteEdit from './pages/QuoteEdit';
import QuotePublic from './pages/QuotePublic';
import Dashboard from './pages/Dashboard';
import Contacts from './pages/Contacts';
import Invoices from './pages/Invoices';
import InvoiceEdit from './pages/InvoiceEdit';
import Bills from './pages/Bills';
import BillEdit from './pages/BillEdit';
import BankAccounts from './pages/BankAccounts';
import BankImport from './pages/BankImport';
import BankReconcile from './pages/BankReconcile';
import VAT201 from './pages/VAT201';
import ReportPL from './pages/ReportPL';
import ReportBalanceSheet from './pages/ReportBalanceSheet';
import ChartOfAccounts from './pages/ChartOfAccounts';
import Settings from './pages/Settings';
import Employees from './pages/Employees';
import Payroll from './pages/Payroll';
import EMP201 from './pages/EMP201';
import EMP501 from './pages/EMP501';
import BankFeeds from './pages/BankFeeds';

function FullPageLoading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-text-dim">
      <div className="w-3 h-3 rounded-full bg-emerald animate-pulse" />
      <div className="font-mono text-xs uppercase tracking-widest">{label}</div>
    </div>
  );
}

export default function App() {
  const { user, loading: authLoading } = useAuth();
  const { currentOrg, loading: orgLoading } = useOrg();

  if (authLoading) return <FullPageLoading label="Checking your session" />;

  // Not signed in. Public invoice + quote views are allowed.
  if (!user) {
    return (
      <Routes>
        <Route path="/p/invoice/:token" element={<InvoicePublic />} />
        <Route path="/p/quote/:token" element={<QuotePublic />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (orgLoading) return <FullPageLoading label="Loading your companies" />;

  // Signed in — Companies portal is the home. Pages inside a company live
  // under the Shell and require a currentOrg.
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/companies" replace />} />
      <Route path="/login" element={<Navigate to="/companies" replace />} />

      {/* Public invoice + quote views — also available while signed in */}
      <Route path="/p/invoice/:token" element={<InvoicePublic />} />
      <Route path="/p/quote/:token" element={<QuotePublic />} />

      {/* Admin portal — always available, doesn't require currentOrg */}
      <Route path="/companies" element={<Companies />} />

      {/* In-company routes — require currentOrg, otherwise bounce to portal */}
      <Route
        path="*"
        element={
          currentOrg ? (
            <Shell>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/contacts/:id/statement" element={<ContactStatement />} />
                <Route path="/services" element={<Services />} />
                <Route path="/quotes" element={<Quotes />} />
                <Route path="/quotes/new" element={<QuoteEdit />} />
                <Route path="/quotes/:id" element={<QuoteEdit />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/invoices/new" element={<InvoiceEdit />} />
                <Route path="/invoices/:id" element={<InvoiceEdit />} />
                <Route path="/bills" element={<Bills />} />
                <Route path="/bills/new" element={<BillEdit />} />
                <Route path="/bills/:id" element={<BillEdit />} />
                <Route path="/bank" element={<BankAccounts />} />
                <Route path="/bank/import" element={<BankImport />} />
                <Route path="/bank/:bankAccountId" element={<BankReconcile />} />
                <Route path="/vat201" element={<VAT201 />} />
                <Route path="/employees" element={<Employees />} />
                <Route path="/payroll" element={<Payroll />} />
                <Route path="/emp201" element={<EMP201 />} />
                <Route path="/emp501" element={<EMP501 />} />
                <Route path="/bank-feeds" element={<BankFeeds />} />
                <Route path="/reports/pl" element={<ReportPL />} />
                <Route path="/reports/balance-sheet" element={<ReportBalanceSheet />} />
                              <Route path="/reports/aging" element={<ReportAging />} />
                <Route path="/accounts" element={<ChartOfAccounts />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Shell>
          ) : (
            <Navigate to="/companies" replace />
          )
        }
      />
    </Routes>
  );
}
